import axios from 'axios';
import { UserPaymentMethod, UserPaymentMethodRequest } from '@finpay-payment-v3/payment-types';
import { getPaymentApiBaseUrl, getPaymentApiPathPrefix, getStripeInstance } from './api-config';


interface SavePaymentMethodParams extends UserPaymentMethodRequest {
  userId: number;
  authToken?: string | null;
}

export const savePaymentMethod = async (
  params: SavePaymentMethodParams
): Promise<UserPaymentMethod> => {
  const {
    userId,
    extPaymentMethodId,
    extPaymentUserId,
    extPaymentIntentId,
    extSetupIntentId,
    paymentMethodTypeId,
    last4,
    cardBrand,
    isVerified = true,
    expMonth,
    expYear,
    authToken,
  } = params;

  const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/user/${userId}/payment-method`;

  const requestBody: UserPaymentMethodRequest = {
    extPaymentMethodId,
    ...(extPaymentUserId && { extPaymentUserId }),
    extPaymentIntentId,
    extSetupIntentId,
    paymentMethodTypeId,
    isActive: true,
    isVerified,
    last4,
    cardBrand,
    ...(expMonth && { expMonth }),
    ...(expYear && { expYear }),
  };

  try {
    const response = await axios.post(url, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          Authorization: `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });

    return response.data;
  } catch (err: any) {
    const responseData = err?.response?.data;
    const message = responseData?.externalResponse?.message || responseData?.message || 'Failed to save payment method.';
    throw new Error(message);
  }
};

interface DeactivatePaymentMethodParams {
  userId: number;
  userPaymentMethodId: number;
  authToken?: string | null;
}

export const deactivatePaymentMethod = async (
  params: DeactivatePaymentMethodParams
): Promise<{ success: boolean; message: string }> => {
  const { userId, userPaymentMethodId, authToken } = params;

  const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/user/${userId}/payment-method/${userPaymentMethodId}`;

  try {
    await axios.patch(
      url,
      { isActive: false },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && {
            Authorization: `Bearer ${authToken}`,
            'x-cog-id': authToken,
          }),
        },
      }
    );

    return { success: true, message: 'Payment method removed.' };
  } catch (err: any) {
    const responseData = err?.response?.data;
    return {
      success: false,
      message: responseData?.externalResponse?.message || responseData?.message || 'Failed to remove payment method.',
    };
  }
};

interface FetchUserPaymentMethodsParams {
  userId: number;
  authToken?: string | null;
}

interface FetchUserPaymentMethodsResult {
  success: boolean;
  message: string;
  paymentMethods: UserPaymentMethod[];
}

export const fetchUserPaymentMethods = async (
  params: FetchUserPaymentMethodsParams
): Promise<FetchUserPaymentMethodsResult> => {
  const { userId, authToken } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/user/${userId}/payment-method`;
    
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          Authorization: `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });

    if (response.status === 200 && Array.isArray(response.data)) {
      return {
        success: true,
        message: 'Payment methods fetched successfully.',
        paymentMethods: response.data as UserPaymentMethod[],
      };
    }

    return {
      success: false,
      message: 'Unexpected response format.',
      paymentMethods: [],
    };
  } catch (err: any) {
    const responseData = err?.response?.data;
    return {
      success: false,
      message: responseData?.externalResponse?.message || responseData?.message || 'Failed to fetch payment methods.',
      paymentMethods: [],
    };
  }
};

interface CreateACHPaymentMethodParams {
  routingNumber: string;
  accountNumber: string;
  accountHolderType?: 'individual' | 'company';
  accountType?: 'checking' | 'savings';
  billingDetails: {
    name: string;
    email: string;
  };
}

interface CreateACHPaymentMethodResult {
  success: boolean;
  message: string;
  paymentMethodId?: string;
  last4?: string;
  fingerprint?: string;
  bankName?: string;
}

export const createACHPaymentMethod = async (
  params: CreateACHPaymentMethodParams
): Promise<CreateACHPaymentMethodResult> => {
  const {
    routingNumber,
    accountNumber,
    accountHolderType = 'individual',
    accountType = 'checking',
    billingDetails,
  } = params;

  try {
    const stripe = await getStripeInstance();
    if (!stripe) {
      return {
        success: false,
        message: 'Stripe has not been initialized.',
      };
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'us_bank_account',
      us_bank_account: {
        routing_number: routingNumber,
        account_number: accountNumber,
        account_holder_type: accountHolderType,
        account_type: accountType,
      },
      billing_details: {
        name: billingDetails.name,
        email: billingDetails.email,
      },
    });

    if (error) {
      return {
        success: false,
        message: error.message || 'Failed to create payment method.',
      };
    }

    if (!paymentMethod) {
      return {
        success: false,
        message: 'Failed to create payment method.',
      };
    }

    return {
      success: true,
      message: 'Bank account verified.',
      paymentMethodId: paymentMethod.id,
      last4: paymentMethod.us_bank_account?.last4 || accountNumber.slice(-4),
      fingerprint: paymentMethod.us_bank_account?.fingerprint,
      bankName: paymentMethod.us_bank_account?.bank_name,
    };
  } catch (err) {
    return {
      success: false,
      message: 'An unexpected error occurred.',
    };
  }
};
